import PickDate from "components/DatePicker/DatePicker";
import CircularProgress from "@mui/material/CircularProgress";
import { LuFileText, LuInfo, LuPlus, LuSave, LuTrash2 } from "react-icons/lu";
import { formatCurrencyAUD } from "utils/formatCurrency";
import InvoiceSelect from "components/FormControls/ThemedSelect";
import CreditLinesViewport from "./CreditLinesViewport";
import "./CreditNoteEditor.css";

const EMPTY_LINE = {
  itemID: "",
  itemName: "",
  itemPrice: "",
  itemDescription: "",
  categoryCodeName: "",
  itemQuantity: "",
  gst: "",
  discountType: "None",
  discountValue: "",
  discountAmount: 0,
  totalItemPrice: 0,
};

export default function CreditNoteEditor({
  creditID,
  values,
  setValues,
  contacts,
  itemOptions,
  lines,
  setLines,
  currencyCode,
  contactNameError,
  onCustomerChange,
  onLineItemChange,
  onLineValueChange,
  onLineDescriptionChange,
  onRemoveLine,
  onCancel,
  onSave,
  onSaveDraft,
  saving,
  savingAction,
  darkMode,
}) {
  const businessName =
    JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "null")
      ?.businessName || "Your business";
  const total = Number(values.finalAmount || 0);
  const currency = currencyCode === "$" || !currencyCode ? "AUD" : currencyCode;
  const money = (amount) => `$${formatCurrencyAUD(amount || 0)}`;
  const visibleLines = lines.map((line, index) => ({ line, index }));

  return (
    <main className={`credit-editor${darkMode ? " is-dark" : ""}`}>
      <div className="credit-editor-topbar">
        <nav aria-label="Breadcrumb">
          <LuFileText aria-hidden="true" />
          <span>Credit note</span>
          <span aria-hidden="true">›</span>
          <strong>{creditID ? "Edit credit note" : "New credit note"}</strong>
        </nav>
        <div className="credit-editor-actions credit-editor-actions-top">
          <button
            type="button"
            className="credit-editor-button secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="credit-editor-button secondary"
            disabled={saving}
            onClick={onSaveDraft}
          >
            {saving && savingAction === "draft" ? (
              <CircularProgress size={16} color="inherit" aria-hidden="true" />
            ) : (
              <LuSave />
            )}
            {saving && savingAction === "draft"
              ? "Saving…"
              : creditID &&
                values.activityStatus &&
                values.activityStatus !== "Draft"
              ? "Save changes"
              : "Save draft"}
          </button>
          <button
            type="button"
            className="credit-editor-button primary"
            disabled={saving}
            onClick={onSave}
          >
            {saving && savingAction === "issue" && (
              <CircularProgress size={16} color="inherit" aria-hidden="true" />
            )}
            {saving && savingAction === "issue"
              ? creditID
                ? "Updating…"
                : "Issuing…"
              : creditID
              ? "Update credit"
              : "Issue credit note"}
          </button>
        </div>
      </div>

      <div className="credit-editor-layout">
        <section className="credit-editor-form">
          <header className="credit-editor-heading">
            <h1>{creditID ? "Edit credit note" : "New credit note"}</h1>
            <p>
              {businessName} · Issued credits · {currency}
            </p>
          </header>

          <div className="credit-editor-field">
            <label htmlFor="credit-customer">Against customer</label>
            <InvoiceSelect
              className="credit-editor-dropdown"
              dark={darkMode}
              id="credit-customer"
              aria-label="Against customer"
              value={values.contactID || ""}
              displayValue={
                contacts.find((entry) => entry._id === values.contactID)
                  ?.companyName || "Select a customer"
              }
              onChange={(event) => {
                const contact = contacts.find(
                  (entry) => entry._id === event.target.value
                );
                onCustomerChange(
                  "",
                  contact
                    ? { label: contact.companyName, value: contact._id }
                    : null
                );
              }}
            >
              <option value="">Select a customer</option>
              {contacts.map((contact) => (
                <option key={contact._id} value={contact._id}>
                  {contact.companyName}
                </option>
              ))}
            </InvoiceSelect>
            {contactNameError && (
              <span className="credit-editor-error">
                Please select a customer.
              </span>
            )}
            <p className="credit-editor-help">
              Choose the customer whose issued amount is being adjusted.
            </p>
          </div>

          <div className="credit-editor-context-card">
            <div>
              <LuFileText aria-hidden="true" />
              <strong>{values.creditNumber || "Auto generated"}</strong>
            </div>
            <span>
              {values.purchaseOrderNumber || "No linked invoice reference"}
            </span>
          </div>

          <section className="credit-editor-section">
            <h2>Credit lines</h2>
            <div className="credit-lines-table">
              <div className="credit-lines-head">
                <span>Description</span>
                <span>Qty</span>
                <span>Unit credit</span>
                <span>Line total</span>
                <span>Remove</span>
              </div>
              <CreditLinesViewport>
              {visibleLines.map(({ line, index }) => (
                <div
                  className="credit-line"
                  key={`${line.itemID || "new"}-${index}`}
                >
                  <label>
                    <span>Line item</span>
                    <InvoiceSelect
                      className="credit-editor-dropdown"
                      dark={darkMode}
                      value={line.itemID || ""}
                      displayValue={line.itemName || "Select an item"}
                      aria-label={`Line ${index + 1} item`}
                      onChange={(event) => {
                        const option = itemOptions.find(
                          (entry) => entry.value === event.target.value
                        );
                        if (option) onLineItemChange("itemID", option, index);
                      }}
                    >
                      <option value="">Select an item</option>
                      {itemOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </InvoiceSelect>
                  </label>
                  <label className="credit-line-description">
                    <span>Description</span>
                    <input
                      value={line.itemDescription || ""}
                      onChange={(event) =>
                        onLineDescriptionChange(
                          "itemDescription",
                          event.target.value,
                          index
                        )
                      }
                      placeholder="Credit description"
                    />
                  </label>
                  <label>
                    <span>Qty</span>
                    <input
                      inputMode="decimal"
                      value={line.itemQuantity || ""}
                      onChange={(event) =>
                        onLineValueChange(
                          "itemQuantity",
                          event.target.value,
                          index
                        )
                      }
                    />
                  </label>
                  <label>
                    <span>Unit credit</span>
                    <input
                      inputMode="decimal"
                      value={line.itemPrice || ""}
                      onChange={(event) =>
                        onLineValueChange(
                          "itemPrice",
                          event.target.value,
                          index
                        )
                      }
                    />
                  </label>
                  <output>{money(line.totalItemPrice)}</output>
                  <button
                    type="button"
                    aria-label={`Remove line ${index + 1}`}
                    disabled={saving || (lines.length === 1 && !line.itemID)}
                    onClick={() => onRemoveLine(index)}
                  >
                    <LuTrash2 />
                  </button>
                  <div className="credit-line-tax-controls">
                    <label>
                      <span>GST rate</span>
                      <InvoiceSelect
                        className="credit-editor-dropdown"
                        dark={darkMode}
                        aria-label={`GST for credit line ${index + 1}`}
                        value={String(line.gst ?? "10")}
                        onChange={(event) =>
                          onLineValueChange("gst", event.target.value, index)
                        }
                      >
                        <option value="10">10% GST</option>
                        <option value="0">No GST</option>
                      </InvoiceSelect>
                    </label>
                    <label>
                      <span>Discount</span>
                      <InvoiceSelect
                        className="credit-editor-dropdown"
                        dark={darkMode}
                        aria-label={`Discount for credit line ${index + 1}`}
                        value={line.discountType || "None"}
                        onChange={(event) =>
                          onLineValueChange(
                            "discountType",
                            event.target.value,
                            index
                          )
                        }
                      >
                        <option value="None">No discount</option>
                        <option value="Percentage">Percentage</option>
                        <option value="Fixed">Fixed amount</option>
                      </InvoiceSelect>
                    </label>
                    {line.discountType && line.discountType !== "None" && (
                      <label>
                        <span>Discount value</span>
                        <input
                          aria-label={`Discount value for credit line ${
                            index + 1
                          }`}
                          inputMode="decimal"
                          value={line.discountValue ?? ""}
                          onChange={(event) =>
                            onLineValueChange(
                              "discountValue",
                              event.target.value,
                              index
                            )
                          }
                        />
                      </label>
                    )}
                  </div>
                </div>
              ))}
              </CreditLinesViewport>
              <div className="credit-lines-total">
                <span>Total credit</span>
                <strong>
                  {money(total)} {currency}
                </strong>
              </div>
            </div>
            <button
              type="button"
              className="credit-editor-add-line"
              onClick={() =>
                setLines((current) => [...current, { ...EMPTY_LINE }])
              }
            >
              <LuPlus />
              Add line item
            </button>
          </section>

          <section className="credit-editor-section">
            <label htmlFor="credit-reference">
              Against invoice / reference
            </label>
            <input
              id="credit-reference"
              value={values.purchaseOrderNumber || ""}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  purchaseOrderNumber: event.target.value,
                }))
              }
              placeholder="Invoice or reference number"
            />
          </section>

          <section className="credit-editor-section">
            <label htmlFor="credit-reason">Reason</label>
            <InvoiceSelect
              className="credit-editor-dropdown"
              dark={darkMode}
              id="credit-reason"
              aria-label="Reason"
              value={values.reason || ""}
              displayValue={values.reason || "Select a reason"}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  reason: event.target.value,
                }))
              }
            >
              <option value="">Select a reason</option>
              <option value="Duplicate line">Duplicate line</option>
              <option value="Pricing correction">Pricing correction</option>
              <option value="Returned goods">Returned goods</option>
              <option value="Service adjustment">Service adjustment</option>
              <option value="Other">Other</option>
            </InvoiceSelect>
          </section>

          <section className="credit-editor-section">
            <h2>Date and tax treatment</h2>
            <label>
              <span>Credit note date</span>
              <PickDate
                selected={
                  values.creditDate ? new Date(values.creditDate) : null
                }
                onChange={(date) =>
                  setValues((current) => ({ ...current, creditDate: date }))
                }
                dateFormat="d MMM yyyy"
              />
            </label>
            <label>
              <span>GST treatment</span>
              <InvoiceSelect
                className="credit-editor-dropdown"
                dark={darkMode}
                aria-label="GST treatment"
                value={values.gstType || "Inclusive"}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    gstType: event.target.value,
                  }))
                }
              >
                <option value="Inclusive">GST inclusive</option>
                <option value="Exclusive">GST exclusive</option>
                <option value="No GST">No GST</option>
              </InvoiceSelect>
            </label>
            <div className="credit-editor-info">
              <LuInfo aria-hidden="true" />
              <p>
                Totals use the selected GST treatment and each line's tax rate
                and discount. The credit amount of
                {` ${money(total)} `}is treated as the total adjustment.
              </p>
            </div>
          </section>

          <section className="credit-editor-section"><label htmlFor="credit-customer-notes">Customer notes / instructions</label><textarea id="credit-customer-notes" value={values.paymentInstruction || ""} onChange={(event) => setValues((current) => ({ ...current, paymentInstruction: event.target.value }))} placeholder="Optional text shown on the credit note" /></section>
          <section className="credit-editor-section">
            <label htmlFor="credit-internal-note">
              Internal notes <small>Optional · Not visible to customer</small>
            </label>
            <textarea
              id="credit-internal-note"
              value={values.internalNotes || ""}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  internalNotes: event.target.value,
                }))
              }
              placeholder="Add a private note for your records..."
            />
          </section>

          <p className="credit-editor-guidance">
            Saving keeps the credit in your existing workflow. Applied credits
            preserve audit history and should not be deleted after delivery.
          </p>
          <div className="credit-editor-actions credit-editor-actions-bottom">
            <button
              type="button"
              className="credit-editor-button secondary"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="credit-editor-button secondary"
              disabled={saving}
              onClick={onSaveDraft}
            >
              {saving && savingAction === "draft" ? (
                <CircularProgress size={16} color="inherit" aria-hidden="true" />
              ) : (
                <LuSave />
              )}
              {saving && savingAction === "draft"
                ? "Saving…"
                : creditID &&
                  values.activityStatus &&
                  values.activityStatus !== "Draft"
                ? "Save changes"
                : "Save draft"}
            </button>
            <button
              type="button"
              className="credit-editor-button primary"
              disabled={saving}
              onClick={onSave}
            >
              {saving && savingAction === "issue" && (
                <CircularProgress size={16} color="inherit" aria-hidden="true" />
              )}
              {saving && savingAction === "issue"
                ? creditID
                  ? "Updating…"
                  : "Issuing…"
                : creditID
                ? "Update credit"
                : "Issue credit note"}
            </button>
          </div>
        </section>

        <aside className="credit-editor-preview">
          <h2>Credit summary</h2>
          <p>Review before saving.</p>
          <div className="credit-editor-preview-card">
            <span>Credit number</span>
            <strong>{values.creditNumber || "Auto generated"}</strong>
            <span>Customer</span>
            <strong>
              {contacts.find((entry) => entry._id === values.contactID)
                ?.companyName || "Not selected"}
            </strong>
          </div>
          <div className="credit-editor-preview-card totals">
            <span>Base amount</span>
            <strong>{money(values.totalAmount)}</strong>
            <span>GST adjustment</span>
            <strong>{money(values.totalGSTAmount)}</strong>
            <span>Total credit</span>
            <strong>
              {money(total)} {currency}
            </strong>
          </div>
          <div className="credit-editor-preview-note">
            <LuInfo />
            <span>
              Amounts remain unchanged until this credit note is successfully
              saved.
            </span>
          </div>
        </aside>
      </div>
    </main>
  );
}
